import React, { useState } from 'react';
import './PersonalForeignObject.css';
import '../styles/ForeignObjectCommon.css';

interface Interest {
    id: string;
    category: string;
    items: string[];
    description?: string;
}

interface PersonalSkill {
    id: string;
    area: string;
    skills: string[];
    level?: string;
}

interface PersonalProject {
    id: string;
    title: string;
    type: string;
    duration?: string;
    description: string[];
    highlights?: string[];
}

interface PersonalForeignObjectProps {
    bounds: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
    groupName: string;
    displayText: string;
    isHovered: boolean;
    isSelected: boolean;
    clipBounds?: boolean;
    leftMargin?: number;
    rightMargin?: number;
    topMargin?: number;
}

// Personal interests and hobbies
const interests: Interest[] = [
    {
        id: 'gaming',
        category: 'Gaming & Technology',
        items: ['Competitive Gaming', 'Game Development', 'Hardware Building', 'Tech Reviews'],
        description: 'Passionate about gaming technology and development'
    },
    {
        id: 'creative',
        category: 'Creative Pursuits',
        items: ['Digital Art', 'Photography', 'Music Production', 'Video Editing'],
        description: 'Exploring creative expression through various digital mediums'
    },
    {
        id: 'learning',
        category: 'Continuous Learning',
        items: ['Online Courses', 'Tech Blogs', 'Open Source', 'Conferences'],
        description: 'Always learning new technologies and best practices'
    }
];

// Personal skills outside of professional work
const personalSkills: PersonalSkill[] = [
    {
        id: 'creative-tech',
        area: 'Creative Technology',
        skills: ['Blender 3D', 'Adobe Creative Suite', 'Unity', 'Unreal Engine'],
        level: 'Intermediate'
    },
    {
        id: 'languages',
        area: 'Languages',
        skills: ['English (Native)', 'Spanish (Conversational)', 'Japanese (Beginner)'],
        level: 'Varied'
    }
];

// Personal projects and achievements
const personalProjects: PersonalProject[] = [
    {
        id: 'portfolio',
        title: 'Interactive Portfolio Website',
        type: 'Web Development',
        duration: '2024 - Present',
        description: [
            'Built this interactive Minecraft-inspired portfolio using React and TypeScript',
            'Implemented custom SVG animations and tile-based rendering system',
            'Designed responsive layout with zoom and pan functionality'
        ],
        highlights: ['React', 'TypeScript', 'SVG Animation', 'Responsive Design']
    },
    {
        id: 'game-project',
        title: 'Indie Game Development',
        type: 'Personal Project',
        duration: '2023 - 2024',
        description: [
            'Developed a 2D puzzle-platformer game using Unity and C#',
            'Created all game assets including sprites, animations, and sound effects',
            'Implemented level editor and custom physics system'
        ],
        highlights: ['Unity', 'C#', 'Game Design', 'Asset Creation']
    }
];

const PersonalForeignObject: React.FC<PersonalForeignObjectProps> = ({ 
    bounds, 
    groupName, 
    displayText, 
    isHovered, 
    isSelected,
    clipBounds = true,
    leftMargin = 32*2,
    rightMargin = 32*2,
    topMargin = 0
}) => {
    const [expandedInterest, setExpandedInterest] = useState<string | null>(null);
    const [expandedSkill, setExpandedSkill] = useState<string | null>(null);
    const [expandedProject, setExpandedProject] = useState<string | null>(null);

    const toggleInterest = (interestId: string) => {
        setExpandedInterest(prev => prev === interestId ? null : interestId);
    };

    const toggleSkill = (skillId: string) => {
        setExpandedSkill(prev => prev === skillId ? null : skillId);
    };

    const toggleProject = (projectId: string) => {
        setExpandedProject(prev => prev === projectId ? null : projectId);
    };

    // Calculate adjusted bounds when clipping is enabled and component is selected
    const getAdjustedBounds = () => {
        if (!isSelected || !clipBounds) {
            return bounds;
        }
        
        return {
            x: bounds.x + leftMargin,
            y: bounds.y + topMargin,
            width: bounds.width - leftMargin - rightMargin,
            height: bounds.height - topMargin
        };
    };

    const adjustedBounds = getAdjustedBounds();

    return (
        <div className="personal-foreign-object header-only foreign-object-size">
            <div className="header-text">{displayText}</div>
            <div className="main-container">
                {/* Add scrollable content here if needed */}
            </div>
        </div>
    );
};

export default PersonalForeignObject;